import { Component, OnInit, computed, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { DividerModule } from 'primeng/divider';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';

// Kanzi
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { OperatingHours } from '@kanzi-apes/kanzi-models';
import { WarehousesQueriesService } from '../../../queries';

@Component({
    selector: 'kanzi-apes-warehouse-detail',
    imports: [
        CommonModule,
        TranslateModule,
        ButtonModule,
        CardModule,
        TagModule,
        SkeletonModule,
        DividerModule,
        ConfirmDialogModule,
        TableModule,
        KanziUiModule,
    ],
    providers: [ConfirmationService, MessageService],
    templateUrl: './warehouse-detail.component.html',
    styleUrls: ['./warehouse-detail.component.scss']
})
export class WarehouseDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private queries = inject(WarehousesQueriesService);
  private confirmService = inject(ConfirmationService);
  private messageService = inject(MessageService);

  // Expose Object to template
  protected readonly Object = Object;

  // Signals
  warehouseId = signal<number | null>(null);
  isQueryEnabled = signal(false);

  // Query for warehouse detail
  warehouseQuery = this.queries.useWarehouseDetail(
    this.warehouseId,
    this.isQueryEnabled
  );

  // Computed signal for the warehouse
  warehouse = computed(() => {
    const isLoading =
      this.warehouseQuery.isPending() || this.warehouseQuery.isFetching();
    return isLoading ? null : this.warehouseQuery.data();
  });

  // Mutation for deletion
  deleteMutation = this.queries.useDeleteWarehouse();

  /**
   * Convert operating_hours object to array for display
   */
  getScheduleArray(operatingHours?: OperatingHours) {
    if (!operatingHours) return [];

    const dayKeys: Array<keyof OperatingHours> = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];

    return dayKeys.map((dayKey, index) => ({
      dayOfWeek: index,
      dayKey,
      hours: operatingHours[dayKey],
    }));
  }

  /**
   * Get translated day name
   */
  getDayName(dayOfWeek: number): string {
    const days = [
      'common.days.monday',
      'common.days.tuesday',
      'common.days.wednesday',
      'common.days.thursday',
      'common.days.friday',
      'common.days.saturday',
      'common.days.sunday',
    ];
    return days[dayOfWeek] || '';
  }

  ngOnInit(): void {
    // Get ID from route parameters
    this.route.params.subscribe((params) => {
      const id = Number(params['id']);
      if (id) {
        this.warehouseId.set(id);
        this.isQueryEnabled.set(true);
      }
    });
  }

  /**
   * Navigate back to the list
   */
  onBack(): void {
    this.router.navigate(['locations', 'warehouses', 'list']);
  }

  /**
   * Navigate to edit
   */
  onEdit(): void {
    const id = this.warehouseId();
    if (id) {
      this.router.navigate(['locations', 'warehouses', 'edit', id]);
    }
  }

  /**
   * Delete warehouse with confirmation
   */
  onDelete(): void {
    const warehouse = this.warehouse();
    if (!warehouse) return;

    this.confirmService.confirm({
      message: `¿Está seguro de eliminar el warehouse "${warehouse.name}"?`,
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      accept: () => {
        if (warehouse.id) {
          this.deleteMutation.mutate(warehouse.id, {
            onSuccess: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Warehouse eliminado',
                detail: `El warehouse "${warehouse.name}" fue eliminado exitosamente`,
              });
              this.onBack();
            },
            onError: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error al eliminar',
                detail:
                  error.message || 'Ocurrió un error al eliminar el warehouse',
              });
            },
          });
        }
      },
    });
  }
}
